import React from 'react';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddPhoto } from '../elements/AddPhoto';
import { InputSelect } from '../elements/InputSelect';
import { InputText } from '../elements/InputText';
import { DropdownValueOption } from 'hooks/useGetOptionStartInspection';
import { lowerCase, startCase } from 'lodash';
import { getNameOption } from 'utils/convertData';
import { SingleEntryField } from 'services/option';
import { DatePicker } from '../elements/DatePicker';
import { useRoomContext } from 'contexts/RoomContext';
import dayjs from 'dayjs';

type Props = {
  index: number;
  remove: (index: number) => void;
  issueTypeOptions: DropdownValueOption[];
  singleEntryField: SingleEntryField[];
  canRemove: boolean;
};

export const RoomLevelIssueItem = ({
  index,
  remove,
  issueTypeOptions,
  canRemove,
  singleEntryField
}: Props) => {
  const { users } = useRoomContext();
  const { control, watch, setValue } = useFormContext();
  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #E0E0E0',
        borderRadius: 2,
        p: 2,
        mb: 3,
        position: 'relative'
      }}>
      {/* DELETE ISSUE BUTTON */}
      {canRemove && (
        <IconButton onClick={() => remove(index)} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <DeleteIcon />
        </IconButton>
      )}

      <Typography fontWeight={600} mb={2}>
        Location Level Issues {index + 1}:
      </Typography>

      <Stack gap={2}>
        {issueTypeOptions?.map((opt) => (
          <React.Fragment key={opt.id}>
            <Controller
              control={control}
              name={`locationDetail.roomLevelIssues.${index}.option_${opt.id}`}
              render={({ field: { onChange, value }, fieldState }) => (
                <InputSelect
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                  inputError={fieldState.error?.message}
                  value={value}
                  placeholder="Select Issue Type"
                  onChange={(val) => {
                    onChange(val);
                  }}
                  options={opt?.dropdownValues?.map((item) => ({
                    label: item.value,
                    value: item.value
                  }))}
                />
              )}
            />

            {watch(`locationDetail.roomLevelIssues.${index}.option_${opt.id}`) === 'OTHER' && (
              <Controller
                name={`locationDetail.roomLevelIssues.${index}.option_${opt.id}_name`}
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    title="Other Issue"
                    placeholder="Enter Other Issue"
                    inputError={fieldState.error?.message}
                    inputProps={{ ...field }}
                  />
                )}
              />
            )}
            {watch(`locationDetail.roomLevelIssues.${index}.option_${opt.id}`) === 'Action By' && (
              <Controller
                name={`locationDetail.roomLevelIssues.${index}.actionBy`}
                control={control}
                render={({ field, fieldState }) => (
                  <InputSelect
                    title="Action By:"
                    placeholder="Select User"
                    value={field.value}
                    inputError={fieldState.error?.message}
                    onChange={field.onChange}
                    options={users?.map((item) => {
                      return {
                        label: item.label,
                        value: item.label
                      };
                    })}
                  />
                )}
              />
            )}
          </React.Fragment>
        ))}
        {singleEntryField?.map((field) => (
          <React.Fragment key={field.id}>
            {field.type === 'DATE' ? (
              <Controller
                name={`locationDetail.roomLevelIssues.${index}.single_${field.id}`}
                control={control}
                render={({ field: f }) => (
                  <DatePicker
                    title={`${field.name}:`}
                    onChange={(val) =>
                      setValue(
                        `locationDetail.roomLevelIssues.${index}.single_${field.id}`,
                        val ? dayjs(val).toISOString() : ''
                      )
                    }
                    value={f.value ? dayjs(f.value) : undefined}
                  />
                )}
              />
            ) : (
              <Controller
                name={`locationDetail.roomLevelIssues.${index}.single_${field.id}`}
                control={control}
                render={({ field: f, fieldState }) => (
                  <InputText
                    title={`${field.name}:`}
                    placeholder={`Enter ${field.name}`}
                    inputProps={{ ...f }}
                    inputError={fieldState.error?.message}
                  />
                )}
              />
            )}
          </React.Fragment>
        ))}
        <Controller
          name={`locationDetail.roomLevelIssues.${index}.desc`}
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              title="Description"
              placeholder="Enter Description"
              inputError={fieldState.error?.message}
              inputProps={{ ...field, multiline: true, minRows: 3 }}
            />
          )}
        />

        {/* PHOTO UPLOAD */}
        <Controller
          name={`locationDetail.roomLevelIssues.${index}.photo`}
          control={control}
          render={({ field, fieldState }) => (
            <AddPhoto
              errorText={fieldState.error?.message}
              label="Issue Photo"
              value={field.value ? field.value : null}
              onChange={field.onChange}
              onRemove={() => field.onChange(null)}
            />
          )}
        />
      </Stack>
    </Box>
  );
};
